/**
 * The ways to pay, as the server offers them — shared by a donation and a
 * tiffin order, which pay through the same rails.
 *
 * Only rails an operator has set up are listed; the app carries no list of its
 * own. Selection is single-choice radio semantics. No card data is collected
 * here: card rails hand off to the provider's own page.
 */

import { Image, StyleSheet, View } from 'react-native';

import { normaliseWalletMsisdn } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { EmptyState, ErrorState } from '../feedback/States';
import { Glyph } from '../common/Icon';
import { RadioGroup } from '../forms/Choice';
import { Field } from '../forms/Field';
import { Skeleton } from '../feedback/Skeleton';
import { Text } from '../common/Text';
import { usePaymentMethods } from '../../core/api/hooks';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

/**
 * Fallback marks, used only where the operator has not set the rail's own
 * logo. `iconUrl` from the server wins wherever it is set.
 */
const METHOD_GLYPH: Record<string, string> = {
  jazzcash: 'mobile',
  easypaisa: 'mobile',
  bank_transfer: 'bank',
  card: 'card',
};

const LOGO = 26;

/** The rails' own marks, from the artwork the charity supplied. */
const METHOD_LOGO: Record<string, number> = {
  jazzcash: require('../../assets/payments/jazzcash.png'),
  easypaisa: require('../../assets/payments/easypaisa.png'),
};

function logoFor(method: { code: string; iconUrl: string | null }) {
  if (method.iconUrl) return { uri: method.iconUrl };
  const bundled = METHOD_LOGO[method.code];
  return bundled ? bundled : null;
}

/** True when the chosen rail can go ahead: Easypaisa needs its account number. */
export function paymentReady(selected: string | null, walletValue: string): boolean {
  if (!selected) return false;
  return selected !== 'easypaisa' || normaliseWalletMsisdn(walletValue) !== null;
}

export interface PaymentMethodPickerProps {
  selected: string | null;
  onSelect: (code: string) => void;
  walletValue: string;
  onWalletChange: (value: string) => void;
  /** Show the wallet number's error; set once the payer has tried to go on. */
  walletTouched: boolean;
  onWalletBlur: () => void;
  onContact: () => void;
  /** Said when nothing is set up: "…Please try again later, or contact us to donate." */
  emptyMessage: string;
}

export function PaymentMethodPicker({
  selected,
  onSelect,
  walletValue,
  onWalletChange,
  walletTouched,
  onWalletBlur,
  onContact,
  emptyMessage,
}: PaymentMethodPickerProps) {
  const t = useT();
  const pick = useLocalized();
  const methods = usePaymentMethods();

  if (methods.isError) {
    return (
      <ErrorState
        title={t('Payment methods unavailable')}
        message={t('We could not load the ways to pay. Please try again.')}
        onRetry={() => void methods.refetch()}
      />
    );
  }

  const enabled = (methods.data ?? []).filter((method) => method.enabled);
  const needsWallet = selected === 'easypaisa';
  const walletValid = normaliseWalletMsisdn(walletValue) !== null;

  if (!methods.isLoading && enabled.length === 0) {
    // Every rail is off in Payment Configuration. Say so, rather than
    // leaving an empty list under a Pay button that never enables.
    return (
      <EmptyState
        title={t('No payment methods available')}
        message={t(emptyMessage)}
        actionLabel={t('Contact Us')}
        onAction={onContact}
      />
    );
  }

  if (methods.isLoading) {
    return (
      <View style={styles.loading}>
        <Skeleton height={64} />
        <Skeleton height={64} />
        <Skeleton height={64} />
      </View>
    );
  }

  return (
    <View style={styles.methods}>
      <RadioGroup
        testID="payment-methods"
        options={enabled.map((method) => ({
          value: method.code,
          label: pick(method.name, method.nameUr),
          description: pick(method.description, method.descriptionUr),
          leading: logoFor(method) ? (
            <Image
              source={logoFor(method)!}
              style={styles.logo}
              resizeMode="contain"
              accessibilityIgnoresInvertColors
            />
          ) : (
            <Glyph name={METHOD_GLYPH[method.code] ?? 'card'} size={22} />
          ),
        }))}
        value={selected}
        onChange={onSelect}
        label={t('Payment method')}
      />

      {needsWallet ? (
        // Easypaisa charges a mobile account approved from that phone, so it
        // needs the number before anything else can happen.
        <Field
          label={t('Easypaisa account number')}
          required
          icon="mobile"
          value={walletValue}
          onChangeText={onWalletChange}
          onBlur={onWalletBlur}
          placeholder="03XX XXXXXXX"
          keyboardType="phone-pad"
          autoComplete="tel"
          hint={t('You will approve the payment on this phone.')}
          error={
            walletTouched && !walletValid
              ? t('Enter the Easypaisa mobile account number, e.g. 03001234567.')
              : null
          }
          testID="field-wallet-msisdn"
          containerStyle={styles.wallet}
        />
      ) : null}
    </View>
  );
}

/** "Your payment is secure…" — closes both payment screens. */
export function PaymentAssurance() {
  const t = useT();
  return (
    <View style={styles.assurance}>
      <Glyph name="privacy" size={18} />
      <Text variant="caption" tone="secondary" style={styles.assuranceText}>
        {t('Your payment is secure and encrypted. We never store your card details.')}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  logo: {
    width: LOGO,
    height: LOGO,
  },
  loading: {
    gap: space.md,
  },
  wallet: {
    marginTop: space.lg,
  },
  methods: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    paddingHorizontal: space.lg,
    marginBottom: space.lg,
  },
  assurance: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginTop: space.lg,
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.brandSubtle,
  },
  assuranceText: {
    flex: 1,
  },
});
